import { useId } from 'react'
import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { isValidToolParameterName } from '@/features/tools/schema'
import type {
  ToolParameterField,
  ToolParameterType,
} from '@/features/tools/types'

const PARAMETER_TYPES: ToolParameterType[] = [
  'string',
  'number',
  'integer',
  'boolean',
  'array',
  'object',
]

export function ParameterListBuilder({
  parameters,
  onChange,
  disabled,
}: {
  parameters: ToolParameterField[]
  onChange: (next: ToolParameterField[]) => void
  disabled?: boolean
}) {
  const fieldId = useId()
  const updateAt = (index: number, patch: Partial<ToolParameterField>) => {
    onChange(
      parameters.map((param, i) =>
        i === index ? { ...param, ...patch } : param
      )
    )
  }
  const removeAt = (index: number) => {
    onChange(parameters.filter((_, i) => i !== index))
  }
  const add = () => {
    onChange([
      ...parameters,
      { name: '', type: 'string', description: '', required: true },
    ])
  }

  return (
    <div className="flex flex-col gap-2">
      {parameters.length === 0 ? (
        <p className="text-sm text-muted-foreground">No parameters defined.</p>
      ) : null}
      {parameters.map((param, index) => {
        const trimmedName = param.name.trim()
        // Blank is fine (parametersToArgsSchema skips it); only flag a
        // non-blank name that would break create_model at runtime.
        const nameError =
          trimmedName && !isValidToolParameterName(trimmedName)
            ? "Must be letters, numbers, or underscores, start with a letter or underscore, and not start with 'model_'."
            : null
        const nameId = `${fieldId}-${index}-name`
        const typeId = `${fieldId}-${index}-type`
        const descriptionId = `${fieldId}-${index}-description`
        return (
          <div
            key={index}
            className="flex min-w-0 shrink-0 flex-col gap-3 rounded-lg border border-neutral-ui-border p-3">
            <div className="grid grid-cols-[minmax(0,1fr)_7rem_auto] items-start gap-2">
              <div className="flex min-w-0 flex-col gap-2">
                <Label
                  htmlFor={nameId}
                  className="text-xs text-muted-foreground">
                  Name
                </Label>
                <Input
                  id={nameId}
                  aria-label="Parameter name"
                  aria-invalid={nameError ? true : undefined}
                  aria-describedby={nameError ? `${nameId}-error` : undefined}
                  placeholder="parameter_name"
                  value={param.name}
                  disabled={disabled}
                  onChange={(e) => updateAt(index, { name: e.target.value })}
                />
              </div>
              <div className="flex min-w-0 flex-col gap-2">
                <Label
                  htmlFor={typeId}
                  className="text-xs text-muted-foreground">
                  Type
                </Label>
                <Select
                  value={param.type}
                  disabled={disabled}
                  onValueChange={(type) =>
                    updateAt(index, { type: type as ToolParameterType })
                  }>
                  <SelectTrigger
                    id={typeId}
                    aria-label="Parameter type"
                    className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PARAMETER_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="self-end text-muted-foreground"
                disabled={disabled}
                aria-label={`Remove parameter ${param.name || index + 1}`}
                onClick={() => removeAt(index)}>
                <Trash2 className="size-4" />
              </Button>
            </div>
            {nameError ? (
              <p id={`${nameId}-error`} className="text-xs text-destructive">
                {nameError}
              </p>
            ) : null}
            <div className="flex flex-col gap-2">
              <Label
                htmlFor={descriptionId}
                className="text-xs text-muted-foreground">
                Description
              </Label>
              <Textarea
                id={descriptionId}
                aria-label="Parameter description"
                placeholder="What value should the assistant provide?"
                value={param.description}
                disabled={disabled}
                rows={2}
                className="min-h-16 resize-y"
                onChange={(e) =>
                  updateAt(index, { description: e.target.value })
                }
              />
            </div>
            <Label className="flex self-start items-center gap-2 text-xs font-normal">
              <Checkbox
                checked={param.required}
                disabled={disabled}
                onCheckedChange={(checked) =>
                  updateAt(index, { required: checked === true })
                }
              />
              Required
            </Label>
          </div>
        )
      })}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={add}
        className="self-start">
        <Plus className="size-4" />
        Add parameter
      </Button>
    </div>
  )
}
